import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Loader2, ShieldCheck } from "lucide-react";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";

export default function EquilibriumLoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!email || !password) {
      setError("Operator credentials required.");
      return;
    }
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Authenticated. Console armed.");
      const dest = location.state?.from?.pathname || "/equilibrium/console";
      navigate(dest, { replace: true });
    } catch (err) {
      const msg = formatApiErrorDetail(err.response?.data?.detail) || err.message;
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      data-testid="eq-login-page"
      className="min-h-screen flex items-center justify-center bg-axiom-bg relative overflow-hidden px-4 py-12"
    >
      <div className="absolute inset-0 bg-grid opacity-40" />
      <div className="absolute inset-0 bg-radial-fade" />

      <div className="relative w-full max-w-md">
        <Link
          to="/equilibrium"
          className="flex items-center gap-3 justify-center mb-10"
        >
          <div className="w-10 h-10 border border-axiom-accent/50 flex items-center justify-center relative">
            <span className="signal-dot animate-pulse-signal" style={{ width: 6, height: 6 }} />
            <span className="absolute inset-0 m-1 border border-axiom-accent/20" />
          </div>
          <div className="leading-tight">
            <div className="font-display text-lg font-bold tracking-tight">EQUILIBRIUM-AI</div>
            <div className="num-label">CONSOLE · ACCESS</div>
          </div>
        </Link>

        <div className="axiom-card p-8">
          <div className="flex items-center gap-2 mb-2">
            <ShieldCheck size={16} className="text-axiom-accent" />
            <span className="num-label">SECURE.OPERATOR.LOGIN</span>
          </div>
          <h1 className="font-display text-3xl text-axiom-text font-bold tracking-tight">
            Operator Authentication
          </h1>
          <p className="mt-2 text-sm text-axiom-muted">
            Provenance ledger, diagnostic intake, and module settings require operator-grade auth.
          </p>

          <form onSubmit={handleSubmit} className="mt-8 space-y-4" data-testid="eq-login-form">
            <label className="block">
              <span className="num-label">EMAIL</span>
              <input
                data-testid="eq-login-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="username"
                className="mt-1.5 w-full bg-axiom-bg border border-white/10 px-4 py-3 text-sm text-axiom-text placeholder:text-axiom-dim focus:border-axiom-accent focus:outline-none"
                placeholder="operator@your-domain.com"
              />
            </label>
            <label className="block">
              <span className="num-label">PASSWORD</span>
              <input
                data-testid="eq-login-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                className="mt-1.5 w-full bg-axiom-bg border border-white/10 px-4 py-3 text-sm text-axiom-text placeholder:text-axiom-dim focus:border-axiom-accent focus:outline-none"
                placeholder="••••••••"
              />
            </label>

            {error && (
              <p data-testid="eq-login-error" className="text-sm text-red-400 font-mono">
                ! {error}
              </p>
            )}

            <button
              type="submit"
              data-testid="eq-login-submit"
              disabled={loading}
              className="btn-axiom w-full justify-center disabled:opacity-60"
            >
              {loading ? (
                <>
                  <Loader2 size={16} className="animate-spin" /> Authenticating...
                </>
              ) : (
                <>
                  Enter Console <ArrowRight size={16} />
                </>
              )}
            </button>
          </form>

          <div className="mt-6 pt-6 border-t border-white/10 text-xs font-mono flex items-center justify-between text-axiom-dim">
            <span>JWT · HS256 · 24h</span>
            <Link to="/equilibrium" className="hover:text-axiom-text">
              ← Platform
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
